import CLUB from '../config/club.js'
import { DEFAULT_STROKE_INDEX, isValidStrokeIndex, strokesOnHole } from './handicapCalc.js'

// Fallback when a tournament has no info.pars stored. Pulled from the active
// club's course config so a fresh tournament defaults to the host course.
export const DEFAULT_PARS = (CLUB.course?.pars && CLUB.course.pars.length === 18)
  ? CLUB.course.pars
  : [4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 4, 3, 5, 4, 4, 3, 4, 5]

// Pull pars from a tournament's info node. Firebase RTDB sometimes returns
// a sparse array as an object keyed by index — normalize both shapes here.
export function parsFor(tournament) {
  const raw = tournament?.info?.pars
  if (!raw) return DEFAULT_PARS
  if (Array.isArray(raw)) return raw.length === 18 ? raw : padToEighteen(raw)
  // object {0:5, 1:3, ...}
  const arr = []
  for (let i = 0; i < 18; i++) arr.push(Number(raw[i]) || 4)
  return arr
}

function padToEighteen(arr) {
  const out = []
  for (let i = 0; i < 18; i++) out.push(Number(arr[i]) || 4)
  return out
}

// ── Net (handicap) scoring — STEP 4, auto-snapshot model ───────────────────
// Used ONLY when the leaderboard NET toggle is on (which appears automatically
// once net data — per-team playingHandicaps — exists). Gross scoring never
// calls these, so gross output stays byte-identical.

// Per-hole stroke index for a tournament: the snapshotted info.strokeIndex
// (validated 18) else the USGA default. Mirrors parsFor.
export function strokeIndexFor(tournament) {
  const raw = tournament?.info?.strokeIndex
  if (Array.isArray(raw) && isValidStrokeIndex(raw.map((n) => Number(n)))) return raw.map(Number)
  // RTDB may hand back a sparse array as an object keyed by index.
  if (raw && typeof raw === 'object') {
    const arr = []
    for (let i = 0; i < 18; i++) arr.push(Number(raw[i]))
    if (isValidStrokeIndex(arr)) return arr
  }
  return DEFAULT_STROKE_INDEX
}

// A NET version of a team's per-hole scores, shaped exactly like the stored
// `scores` object (each hole → { bestBall }), so it drops into the same
// teamVsPar / rankTeams / headToHeadStatus / matchPlayResult path unchanged.
// Net best ball per hole = min over the two players of (gross − strokesReceived),
// using team.info.playingHandicaps and the course stroke index. Falls back to
// the stored gross scores when the team has no playingHandicaps (so a net view
// degrades to gross for that team rather than blanking it out).
export function netScoresFor(team, strokeIndex = DEFAULT_STROKE_INDEX) {
  const ph = team?.info?.playingHandicaps
  const scores = team?.scores || {}
  if (!Array.isArray(ph) || ph.length === 0) return scores
  const ph1 = Number(ph[0])
  const ph2 = Number(ph[1])
  const out = {}
  for (let h = 1; h <= 18; h++) {
    const s = scores[h]
    if (!s) continue
    const hi = h - 1
    const p1 = Number(s.p1score)
    const p2 = Number(s.p2score)
    const has1 = Number.isFinite(p1) && p1 > 0
    const has2 = Number.isFinite(p2) && p2 > 0
    let net = null
    if (has1 && has2) {
      const n1 = p1 - strokesOnHole(ph1, hi, strokeIndex)
      const n2 = p2 - strokesOnHole(ph2, hi, strokeIndex)
      net = Math.min(n1, n2)
    } else if (has1) {
      net = p1 - strokesOnHole(ph1, hi, strokeIndex)
    } else if (has2) {
      net = p2 - strokesOnHole(ph2, hi, strokeIndex)
    } else if (Number.isFinite(Number(s.bestBall))) {
      // Single combined gross with no per-player split — give the hole's
      // stroke to the higher-handicap player (the common convention).
      net = Number(s.bestBall) - strokesOnHole(Math.max(ph1, ph2), hi, strokeIndex)
    }
    if (net != null) out[h] = { ...s, bestBall: net }
  }
  return out
}

// Does this tournament have net data ready? True once any team carries a
// non-empty playingHandicaps array — which is what the auto-snapshot writes
// on flight-lock (or that a demo pre-stamps). Drives the leaderboard toggle.
export function netAvailableFor(tournament) {
  const flights = tournament?.flights || {}
  for (const f of Object.values(flights)) {
    for (const t of Object.values(f?.teams || {})) {
      const ph = t?.info?.playingHandicaps
      if (Array.isArray(ph) && ph.some((x) => x != null)) return true
    }
  }
  return false
}

export function totalPar(pars = DEFAULT_PARS) {
  let n = 0
  for (let i = 0; i < 18; i++) n += pars[i] || 4
  return n
}

export function teamThru(scores) {
  if (!scores) return 0
  let count = 0
  for (let h = 1; h <= 18; h++) {
    if (scores[h] && Number.isFinite(scores[h].bestBall)) count++
  }
  return count
}

export function teamGross(scores) {
  if (!scores) return 0
  let total = 0
  for (let h = 1; h <= 18; h++) {
    const s = scores[h]
    if (s && Number.isFinite(s.bestBall)) total += s.bestBall
  }
  return total
}

export function teamVsPar(scores, pars = DEFAULT_PARS) {
  if (!scores) return 0
  let diff = 0
  for (let h = 1; h <= 18; h++) {
    const s = scores[h]
    if (s && Number.isFinite(s.bestBall)) {
      diff += s.bestBall - (pars[h - 1] || 4)
    }
  }
  return diff
}

export function teamNet(scores, handicap = 0) {
  return teamGross(scores) - (Number(handicap) || 0)
}

// Scramble per-player handicap weights, by team size. Lowest-handicap player
// gets the highest weight (sorted ascending). The 2-man row (35% / 15%) is
// fixed by user spec; 1/3/4-man use standard USGA Rule 9-style weights as
// sensible defaults — admin can edit per-player handicaps to tune outcome.
// USGA/GG standard scramble handicap allowances (lowest handicap gets the
// largest share). Affects ONLY the scramble NET column via
// scrambleHandicapStrokes → teamScrambleNet; gross scoring never reads it.
const SCRAMBLE_HCP_WEIGHTS = {
  1: [1.00],
  2: [0.35, 0.15],
  3: [0.30, 0.20, 0.10],
  4: [0.25, 0.20, 0.15, 0.10],
}

// Strokes a Scramble team receives, computed from its per-player handicaps
// (team.info.handicaps array). Returns 0 when no per-player handicaps are
// stored — in that case callers should fall back to teamNet's single
// team-handicap field. Result is rounded to the nearest whole stroke.
export function scrambleHandicapStrokes(team) {
  const hcps = (team?.info?.handicaps || [])
    .map(Number)
    .filter(Number.isFinite)
  if (hcps.length === 0) return 0
  const weights = SCRAMBLE_HCP_WEIGHTS[hcps.length] || SCRAMBLE_HCP_WEIGHTS[4]
  const sorted = [...hcps].sort((a, b) => a - b)
  let total = 0
  for (let i = 0; i < sorted.length; i++) total += sorted[i] * (weights[i] || 0)
  return Math.round(total)
}

// Scramble net = gross - team strokes. Use this whenever a team has the
// per-player `info.handicaps` array; for 4-Ball / Stroke teams, prefer
// the legacy teamNet(scores, info.handicap) path.
export function teamScrambleNet(team, scores) {
  return teamGross(scores) - scrambleHandicapStrokes(team)
}

// Sort live-leaderboard order: most under par at the top, least under par
// at the bottom. Teams that haven't started yet always sort last (a "+0"
// unstarted team shouldn't sit in the middle of -3 / +1 played teams).
// During the round this means a -8 thru 12 team ranks ABOVE a -7 thru 18
// team — the standard golf-leaderboard convention. After everyone plays
// 18 holes the vs-par sort produces the same final order as gross.
//
// Tie-break: more holes played (deeper into the round when tied), then
// scorecard playoff back from hole 18, then alphabetical. Net column
// prefers the Scramble per-player handicap formula when info.handicaps
// is present; falls back to the legacy single-team handicap otherwise.
export function rankTeams(teams, useNet, pars = DEFAULT_PARS) {
  const rows = teams.map((t) => {
    const useScrambleNet = Array.isArray(t.info?.handicaps) && t.info.handicaps.length > 0
    const hcpStrokes = useScrambleNet
      ? scrambleHandicapStrokes(t)
      : (Number(t.info?.handicap) || 0)
    const vsPar = teamVsPar(t.scores, pars)
    const net = useScrambleNet
      ? teamScrambleNet(t, t.scores)
      : teamNet(t.scores, t.info?.handicap)
    return {
      ...t,
      thru: teamThru(t.scores),
      gross: teamGross(t.scores),
      vsPar,
      netVsPar: vsPar - hcpStrokes,
      net,
      hcpStrokes,
    }
  })
  rows.sort((a, b) => {
    // Unstarted teams always sort last so a thru-0 "even" team doesn't
    // appear above a played team that's at +2 or +5.
    const aStarted = a.thru > 0
    const bStarted = b.thru > 0
    if (aStarted !== bStarted) return aStarted ? -1 : 1

    // Primary key: vs-par (or net-vs-par when handicaps are on). Lower wins.
    const av = useNet ? a.netVsPar : a.vsPar
    const bv = useNet ? b.netVsPar : b.vsPar
    if (av !== bv) return av - bv

    // Same vs-par → more holes played ranks higher.
    if (a.thru !== b.thru) return b.thru - a.thru

    // Scorecard playoff: count back from hole 18 to break a tie. Lower
    // hole-N score wins; if both are tied or unscored, keep going to 17,
    // 16, …, 1. Falls through to alphabetical only on a true full-card
    // tie. Per the user's spec — standard club tournament tie-breaker.
    for (let h = 18; h >= 1; h--) {
      const ah = a.scores?.[h]?.bestBall
      const bh = b.scores?.[h]?.bestBall
      const aHas = Number.isFinite(ah)
      const bHas = Number.isFinite(bh)
      if (aHas && bHas && ah !== bh) return ah - bh
    }
    return (a.info?.name || '').localeCompare(b.info?.name || '')
  })
  return rows
}

export function autoAssignFlights(players, flightCount) {
  // Sort by handicap ascending; deal round-robin into flights.
  const sorted = [...players].sort((a, b) => (a.handicap ?? 99) - (b.handicap ?? 99))
  const buckets = Array.from({ length: flightCount }, () => [])
  sorted.forEach((p, i) => buckets[i % flightCount].push(p))
  return buckets
}

// Hole-by-hole rotation that wraps after 18 → 1 (shotgun start support).
export function nextHole(h) { return h >= 18 ? 1 : h + 1 }
export function prevHole(h) { return h <= 1 ? 18 : h - 1 }

// Per-team match-play standings for a flight. Each team's status comes from
// its head-to-head vs its assigned opponent (info.opponentId). Returns rows
// sorted by lead size (UP - DOWN) then by holes played.
export function matchPlayFlightStandings(teams) {
  const byId = Object.fromEntries(teams.map((t) => [t.id, t]))
  const rows = teams.map((t) => {
    const oppId = t.info?.opponentId
    const opp = oppId ? byId[oppId] : null
    const h2h = opp ? { ...require_h2h(t, opp) } : { myWins: 0, oppWins: 0, played: 0 }
    const gap = h2h.myWins - h2h.oppWins
    return { ...t, opponent: opp, ...h2h, gap }
  })
  rows.sort((a, b) => {
    if (b.gap !== a.gap) return b.gap - a.gap
    if (b.played !== a.played) return b.played - a.played
    return (a.info?.name || '').localeCompare(b.info?.name || '')
  })
  return rows
}

// Local copy to avoid forward-ref ordering issues in some bundlers.
function require_h2h(myTeam, oppTeam) {
  let myWins = 0, oppWins = 0, played = 0
  for (let h = 1; h <= 18; h++) {
    const my = myTeam?.scores?.[h]?.bestBall
    const opp = oppTeam?.scores?.[h]?.bestBall
    if (!Number.isFinite(my) || !Number.isFinite(opp)) continue
    played++
    if (my < opp) myWins++
    else if (opp < my) oppWins++
  }
  return { myWins, oppWins, played }
}

// Dormie / clinched / halved status from a head-to-head tally. Caller supplies
// myWins / oppWins / played (from headToHeadStatus). Returns:
//
//   final     true once the match is mathematically decided OR all 18 holes
//             have been scored.
//   winner    'me' | 'opp' | 'tie'  (relative to whichever team is "my" side)
//   lead      |gap|, holes ahead (absolute value)
//   remaining 18 - played
//   resultText  "3&2" | "1 up" | "Halved"  — populated only when final
//   statusText  "1 UP THRU 14" | "AS THRU 14"  — populated only when live
//
// Clinch rule: lead > remaining holes. Example 3 UP with 2 to play → "Won 3&2"
// even before holes 17/18 are entered.
export function matchPlayResult({ myWins = 0, oppWins = 0, played = 0 } = {}) {
  const gap = myWins - oppWins
  const lead = Math.abs(gap)
  const remaining = Math.max(0, 18 - played)
  const winner = gap === 0 ? 'tie' : (gap > 0 ? 'me' : 'opp')

  if (lead > remaining) {
    const resultText = remaining === 0 ? `${lead} up` : `${lead}&${remaining}`
    return { final: true, suddenDeath: false, winner, lead, remaining, played, gap, resultText, statusText: '' }
  }
  // A tie is NOT final. Match play goes to sudden death — they keep playing
  // extra holes (19, 20, …) until one side wins a hole. `final` stays false so
  // nothing declares a halve or advances the bracket on a tie.
  if (played >= 18 && lead === 0) {
    return { final: false, suddenDeath: true, winner: 'tie', lead: 0, remaining, played, gap: 0, resultText: '', statusText: `ALL SQUARE THRU ${Math.min(played, 18)} — SUDDEN DEATH` }
  }
  const statusText = lead === 0 ? `AS THRU ${played}` : `${lead} UP THRU ${played}`
  return { final: false, suddenDeath: false, winner, lead, remaining, played, gap, resultText: '', statusText }
}

export function fmtMatchStatus(gap, played) {
  if (played === 0) return '—'
  if (gap === 0) return `AS thru ${played}`
  if (gap > 0) return `${gap} UP thru ${played}`
  return `${-gap} DOWN thru ${played}`
}

export function fmtVsPar(n) {
  if (n === 0) return 'E'
  return n > 0 ? `+${n}` : `${n}`
}

// 'match' (default for most club 4-Ball Best Ball play) or 'stroke'.
// Flight setting overrides the tournament-level setting.
export function getPlayStyle(tournament, flight) {
  return (
    flight?.info?.playStyle ||
    tournament?.info?.playStyle ||
    'match'
  )
}

// Pair teams within a flight: teams[0] vs teams[1], teams[2] vs teams[3], etc.
// Honors an explicit info.opponentId if the admin has set one. Returns the
// opponent's { id, info, scores } or null when there's no partner (odd team
// out) or the team isn't found.
export function findOpponent(flight, myTeamId) {
  if (!flight?.teams) return null
  const entries = Object.entries(flight.teams)
  const myEntry = entries.find(([id]) => id === myTeamId)
  if (!myEntry) return null
  // Explicit assignment wins.
  const explicit = myEntry[1]?.info?.opponentId
  if (explicit && flight.teams[explicit]) {
    return { id: explicit, ...flight.teams[explicit] }
  }
  // Otherwise pair by index: 0↔1, 2↔3, 4↔5, ...
  const idx = entries.findIndex(([id]) => id === myTeamId)
  const partnerIdx = idx % 2 === 0 ? idx + 1 : idx - 1
  if (partnerIdx < 0 || partnerIdx >= entries.length) return null
  const [oppId, oppData] = entries[partnerIdx]
  return { id: oppId, ...oppData }
}

// Head-to-head match-play tally between two teams. Counts only holes where
// both teams have a best ball. Halved holes credit no one.
export function headToHeadStatus(myTeam, oppTeam) {
  let myWins = 0
  let oppWins = 0
  let played = 0
  // Count regulation holes 1-18 plus any sudden-death playoff holes (19+) that
  // both teams have scored, in order, so a playoff decides the match.
  const holeSet = new Set()
  for (const k of Object.keys(myTeam?.scores || {})) holeSet.add(Number(k))
  for (const k of Object.keys(oppTeam?.scores || {})) holeSet.add(Number(k))
  const holes = [...holeSet].filter((h) => Number.isFinite(h) && h >= 1).sort((a, b) => a - b)
  for (const h of holes) {
    const my = myTeam?.scores?.[h]?.bestBall
    const opp = oppTeam?.scores?.[h]?.bestBall
    if (!Number.isFinite(my) || !Number.isFinite(opp)) continue
    played++
    if (my < opp) myWins++
    else if (opp < my) oppWins++
  }
  return { myWins, oppWins, played }
}

// Match-play standings across a flight. Each hole is awarded to the team with
// the strictly-lowest best ball; ties on a hole are "halved" and credit no
// one. Returns teams sorted by holes won (descending). Recomputed on every
// render so a team's hole-win count updates correctly when another team later
// matches their score on the same hole.
export function matchPlayStandings(teams) {
  const won = Object.fromEntries(teams.map((t) => [t.id, 0]))
  for (let h = 1; h <= 18; h++) {
    let bestScore = Infinity
    let winners = []
    for (const t of teams) {
      const s = t.scores?.[h]?.bestBall
      if (!Number.isFinite(s)) continue
      if (s < bestScore) {
        bestScore = s
        winners = [t.id]
      } else if (s === bestScore) {
        winners.push(t.id)
      }
    }
    if (winners.length === 1) won[winners[0]] += 1
  }
  return teams
    .map((t) => ({ ...t, holesWon: won[t.id] }))
    .sort((a, b) => b.holesWon - a.holesWon)
}
